import { SwapHoriz } from 'components/icons';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ActiveCoverDto } from 'types/matters';
import { t } from 'utils/getTranslations';
import { displayName, useLastNameFirst } from 'utils/nameOrder';

const ROLE_LABEL_IDS: Record<ActiveCoverDto['role'], string> = {
    SUPERVISING_PARTNER: 'matters.supervising-partner',
    RESPONSIBLE_LAWYER: 'matters.responsible-lawyer',
};

/**
 * The substitute standing in for a case's lead, after the lead's name. A button opening a hover card rather than a
 * tooltip, because the card holds a link to the delegation.
 */
export default function CoverMarker({ cover }: { cover: ActiveCoverDto }) {
    const dateFormat = useEffectiveDateFormat();
    const lastNameFirst = useLastNameFirst();
    const substitute = displayName({ ...cover.employee, onVacation: false }, lastNameFirst);
    const until = format(parseISO(cover.endDate), dateFormat);

    return (
        <Popover>
            <PopoverTrigger
                openOnHover
                aria-label={t('matters.cover.label', { substitute, date: until })}
                // The 20px line widens its hit area to the 24px a target needs. Text rather than a flex row, so a long
                // name wraps between its words and the date stays whole.
                className="text-fg-secondary hover:text-fg new:text-foreground-secondary relative rounded-sm text-left text-sm after:absolute after:inset-x-0 after:-inset-y-0.5"
            >
                <SwapHoriz className="mr-1 inline-block size-4 align-text-bottom" />
                <span className="text-fg">{substitute}</span>{' '}
                <span className="whitespace-nowrap">{t('matters.cover.until', { date: until })}</span>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[300px] gap-1.5 p-3">
                <span>
                    {t('matters.cover.card', {
                        substitute,
                        role: t(ROLE_LABEL_IDS[cover.role]),
                        start: format(parseISO(cover.startDate), dateFormat),
                        end: until,
                    })}
                </span>
                {cover.delegationId !== null ? (
                    <LinkWithOrigin
                        to={`/delegations/${cover.delegationId}`}
                        className="text-link w-fit text-sm font-medium hover:underline"
                    >
                        {t('matters.cover.open-delegation')}
                    </LinkWithOrigin>
                ) : null}
            </PopoverContent>
        </Popover>
    );
}
